"use client";

import { useQuery } from "@tanstack/react-query";

import { useEffect, useMemo, useState } from "react";

import Button from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectTrigger,
} from "@/components/ui/multi-select";

import { useDebounce } from "@/hooks/use-debounce";
import { useEventTitle } from "@/hooks/use-event-title";

import { formatDate, formatTime } from "@/lib/format-data";

import { EventSegmentType } from "@/types/enums";
import type { EventDetail } from "@/types/event";

import { useUpdateNoteEventLinkMutation } from "@/api/provider/scribe/note-mutation";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";

function formatEventOption(
  ev: EventDetail,
  getEventTitle: (event: EventDetail) => string,
): string {
  const when = `${formatDate(ev.start, true, true)} ${formatTime(ev.start)}`;
  const services = ev.segments
    .filter((s) => s.type === EventSegmentType.SERVICE)
    .map((s) => s.service.name)
    .filter(Boolean)
    .join(", ");
  const title = getEventTitle(ev);
  const hasStoredTitle = !!ev.title?.trim();
  const parts = [
    when,
    title || services,
    hasStoredTitle ? services : "",
  ].filter(Boolean);
  return parts.join(" — ");
}

export interface LinkNoteToAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noteId: string;
  patientId: string;
  currentEventId?: string | null;
}

export default function LinkNoteToAppointmentDialog({
  open,
  onOpenChange,
  noteId,
  patientId,
  currentEventId,
}: LinkNoteToAppointmentDialogProps) {
  const getEventTitle = useEventTitle();
  const [dropdownSearch, setDropdownSearch] = useState("");
  const debouncedSearch = useDebounce(dropdownSearch, 300);
  const [selected, setSelected] = useState<string[]>([]);
  const [multiSelectKey, setMultiSelectKey] = useState(0);

  const { data: events, isFetching } = useQuery({
    ...eventQueryOptions.searchPatientEvents(patientId, debouncedSearch),
    enabled: open && !!patientId,
  });

  const options = useMemo(() => {
    const rows = events ?? [];
    const fromApi = rows.map((ev) => ({
      value: ev.id,
      label: formatEventOption(ev, getEventTitle),
    }));
    const ids = new Set(fromApi.map((o) => o.value));
    if (currentEventId && !ids.has(currentEventId)) {
      return [
        {
          value: currentEventId,
          label: "Currently linked appointment (search to replace)",
        },
        ...fromApi,
      ];
    }
    return fromApi;
  }, [events, currentEventId, getEventTitle]);

  useEffect(() => {
    if (!open) return;
    setDropdownSearch("");
    setMultiSelectKey((k) => k + 1);
    setSelected(currentEventId ? [currentEventId] : []);
  }, [open, currentEventId]);

  const mutation = useUpdateNoteEventLinkMutation();

  const handleSave = () => {
    const eventId = selected.length > 0 ? selected[0]! : null;
    const picked =
      eventId && events ? events.find((ev) => ev.id === eventId) : undefined;
    onOpenChange(false);
    mutation.mutate({
      noteId,
      eventId,
      optimisticVisitDate: picked?.start ?? null,
    });
  };

  const handleRemove = () => {
    onOpenChange(false);
    mutation.mutate({ noteId, eventId: null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link appointment</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-body text-foreground-secondary">
            Open the list below and search by date, title, service name, or
            provider name. Choose one appointment to link to this note (every
            version will use the same link).
          </p>
          <div className="space-y-1">
            <Label>Select appointment</Label>
            <MultiSelect
              key={multiSelectKey}
              options={options}
              value={selected}
              onChange={setSelected}
              maxSelections={1}
              className="w-full"
              remoteSearch
              onSearchTermChange={setDropdownSearch}
            >
              <MultiSelectTrigger
                placeholder={isFetching ? "Loading…" : "Choose an appointment…"}
              />
              <MultiSelectContent
                searchPlaceholder="e.g. 4/13/2026, Botox, or provider name"
                emptyMessage={
                  isFetching ? "Loading…" : "No appointments match your search."
                }
              />
            </MultiSelect>
          </div>
        </DialogBody>
        <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {currentEventId ? (
              <Button
                className="text-status-error-text"
                type="button"
                variant="ghost"
                onClick={handleRemove}
              >
                Remove link
              </Button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
